import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import dayjs from "dayjs";
import { Select, DatePicker, TimePicker, message } from "antd";

const { Option } = Select;

const INCIDENT_TYPES = [
  { value: "Bruit",                        label: "🔊 Bruit" },
  { value: "Harcèlement sexuel",           label: "⚠️ Harcèlement sexuel" },
  { value: "Prise électrique défectueuse", label: "🔌 Prise défectueuse" },
  { value: "Lampe ne fonctionne pas",      label: "💡 Lampe HS" },
  { value: "Autre",                        label: "📋 Autre" },
];

export default function Incidents() {
  const [mode, setMode] = useState(""); // '' | 'reporter'
  const [tableInput, setTableInput] = useState("");
  const [tableError, setTableError] = useState(false);
  const [tables, setTables] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");

  const [form, setForm] = useState({
    type: "",
    tablesConcernees: [],
    dateIncident: dayjs(),
    heureIncident: dayjs(),
    commentaire: "",
  });

  const toggleTheme = () => {
    setDark(d => {
      localStorage.setItem("ws-theme", !d ? "dark" : "light");
      return !d;
    });
  };

  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    setTables(Array.from({ length: 57 }, (_, i) => i + 1));
  }, []);

  const handleTableDigit = (digit) => {
    const next = tableInput + digit;
    if (next.length > 2) return; // max 2 digits
    setTableInput(next);
    setTableError(false);
  };

  const handleTableDelete = () => {
    setTableInput(prev => prev.slice(0, -1));
    setTableError(false);
  };

  const handleTableConfirm = () => {
    const num = Number(tableInput);
    if (!tableInput || num < 1 || num > 57) {
      setTableError(true);
      return;
    }
    setForm(f => ({ ...f, tablesConcernees: [num] }));
    setMode("reporter");
  };

  const resetForm = () => {
    setForm({ type: "", tablesConcernees: [], dateIncident: dayjs(), heureIncident: dayjs(), commentaire: "" });
    setTableInput("");
    setTableError(false);
    setSubmitted(false);
    setMode("");
  };

  const handleSubmit = async () => {
    if (!form.type || form.tablesConcernees.length === 0) {
      message.error("Veuillez remplir les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "incidents"), {
        tableSource: form.tablesConcernees[0],
        type: form.type,
        tablesConcernees: form.tablesConcernees,
        dateIncident: form.dateIncident.toDate(),
        heureIncident: form.heureIncident.toDate(),
        commentaire: form.commentaire,
        anonymous: true,
        createdAt: new Date(),
      });

      await fetch("https://ntfy.sh/westudy_incidents", {
        method: "POST",
        headers: {
          "Title": "Nouveau incident signalé",
          "Priority": "high",
          "Content-Type": "text/plain; charset=utf-8",
        },
        body: `🚨 Incident signalé — Table ${form.tablesConcernees[0]}\nType: ${form.type}\nTables: ${form.tablesConcernees.join(", ")}\n${form.commentaire ? `Commentaire: ${form.commentaire}` : ""}`,
      });

      setSubmitted(true);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de l'enregistrement.");
    }
    setSubmitting(false);
  };

  // ── Home screen — numpad ─────────────────────────────────
  if (!mode) {
    const digits = ["1","2","3","4","5","6","7","8","9","","0","⌫"];
    return (
      <div style={{ background: t.bg, minHeight: "100vh" }}>
        <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>

          {/* Topbar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0" }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 32, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
          </div>

          {/* Header */}
          <div style={{ padding: "32px 18px 8px", textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: t.text2, marginBottom: 6 }}>Signalement d'incident</div>
            <div style={{ fontSize: 15, color: t.text2 }}>Entrez le numéro de votre table</div>
          </div>

          {/* Display */}
          <div style={{ padding: "20px 40px 0", textAlign: "center" }}>
            <div style={{
              background: t.cardBg, border: `2px solid ${tableError ? t.red : tableInput ? t.blue : t.border}`,
              borderRadius: 16, padding: "20px 24px", marginBottom: 6, transition: "border-color 0.15s",
            }}>
              <div style={{ fontSize: 72, fontWeight: 800, color: tableInput ? t.text1 : t.text3, lineHeight: 1, letterSpacing: "-2px", minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {tableInput || <span style={{ fontSize: 40, color: t.text3 }}>—</span>}
              </div>
            </div>
            {tableError && (
              <div style={{ fontSize: 12, color: t.red, marginBottom: 4 }}>Numéro invalide (1 – 57)</div>
            )}
            <div style={{ fontSize: 11, color: t.text3 }}>Tables disponibles : 1 à 57</div>
          </div>

          {/* Numpad */}
          <div style={{ padding: "20px 40px 0" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              {digits.map((d, i) => {
                if (d === "") return <div key={i} />;
                const isDelete = d === "⌫";
                return (
                  <button
                    key={i}
                    onClick={() => isDelete ? handleTableDelete() : handleTableDigit(d)}
                    style={{
                      height: 64, borderRadius: 14, border: `1px solid ${t.border}`,
                      background: isDelete ? t.redBg : t.cardBg,
                      color: isDelete ? t.red : t.text1,
                      fontSize: isDelete ? 20 : 24, fontWeight: 600,
                      cursor: "pointer", transition: "background 0.1s",
                    }}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Confirm button */}
          <div style={{ padding: "16px 40px 0" }}>
            <button
              onClick={handleTableConfirm}
              disabled={!tableInput}
              style={{ ...s.primaryBtn, background: tableInput ? t.red : t.bg3, color: tableInput ? "#fff" : t.text3, padding: "14px", fontSize: 15, opacity: tableInput ? 1 : 0.6 }}
            >
              🚨 Signaler un incident
            </button>
          </div>

          {/* Anonymous note */}
          <div style={{ margin: "16px 40px 0", padding: "10px 14px", background: t.bg3, borderRadius: 12, fontSize: 12, color: t.text2, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <span>🔒</span> Signalement 100% anonyme
          </div>
        </div>
      </div>
    );
  }

  // ── Incident form ────────────────────────────────────────
  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={resetForm} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
              ← Retour
            </button>
            <div style={{ fontSize: 15, fontWeight: 600, color: t.text1 }}>Signaler un incident</div>
          </div>
          <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
            <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
          </button>
        </div>

        {/* Badges row */}
        <div style={{ padding: "14px 18px 0", display: "flex", gap: 8 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: t.redBg, border: `1px solid ${t.redBorder}`, borderRadius: 20, padding: "4px 12px" }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: t.red }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: t.red }}>Table {form.tablesConcernees[0]}</span>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: t.bg3, border: `1px solid ${t.border}`, borderRadius: 20, padding: "4px 12px" }}>
            <span style={{ fontSize: 12 }}>🔒</span>
            <span style={{ fontSize: 12, color: t.text2 }}>Anonyme</span>
          </div>
        </div>

        {submitted ? (
          // ── Success state ──
          <div style={{ padding: "48px 18px 0", textAlign: "center" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: t.greenBg, border: `2px solid ${t.green}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, margin: "0 auto 20px" }}>
              ✅
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Incident signalé !</div>
            <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6, marginBottom: 28, maxWidth: 300, margin: "0 auto 28px" }}>
              Notre équipe a été notifiée et va prendre en charge votre signalement.
            </div>
            <button onClick={resetForm} style={{ ...s.primaryBtn, background: t.blue, padding: "12px 32px", width: "auto" }}>
              ← Retour
            </button>
          </div>
        ) : (
          // ── Form ──
          <div style={{ padding: "16px 18px 0", display: "flex", flexDirection: "column", gap: 12 }}>

            {/* Type */}
            <div style={{ ...s.fieldCard, background: t.cardBg, border: `1px solid ${t.border}` }}>
              <label style={{ ...s.label, color: t.text2 }}>Type d'incident <span style={{ color: t.red }}>*</span></label>
              <Select
                placeholder="Sélectionnez un type"
                size="large"
                style={{ width: "100%" }}
                value={form.type || undefined}
                onChange={value => setForm({ ...form, type: value })}
              >
                {INCIDENT_TYPES.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>
            </div>

            {/* Tables */}
            <div style={{ ...s.fieldCard, background: t.cardBg, border: `1px solid ${t.border}` }}>
              <label style={{ ...s.label, color: t.text2 }}>Tables concernées <span style={{ color: t.red }}>*</span></label>
              <Select
                mode="multiple"
                placeholder="Sélectionnez une ou plusieurs tables"
                size="large"
                style={{ width: "100%" }}
                value={form.tablesConcernees}
                onChange={values => setForm({ ...form, tablesConcernees: values })}
                maxTagCount="responsive"
              >
                {tables.map(tbl => (
                  <Option key={tbl} value={tbl}>Table {tbl}</Option>
                ))}
              </Select>
              <div style={{ fontSize: 11, color: t.text3, marginTop: 6 }}>
                Table {form.tablesConcernees[0]} pré-sélectionnée · Vous pouvez en ajouter d'autres
              </div>
            </div>

            {/* Date + Time */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ ...s.fieldCard, background: t.cardBg, border: `1px solid ${t.border}` }}>
                <label style={{ ...s.label, color: t.text2 }}>📅 Date</label>
                <DatePicker
                  size="large" style={{ width: "100%" }}
                  value={form.dateIncident}
                  onChange={date => setForm({ ...form, dateIncident: date })}
                  format="DD/MM/YYYY"
                />
              </div>
              <div style={{ ...s.fieldCard, background: t.cardBg, border: `1px solid ${t.border}` }}>
                <label style={{ ...s.label, color: t.text2 }}>🕐 Heure</label>
                <TimePicker
                  size="large" style={{ width: "100%" }}
                  value={form.heureIncident}
                  onChange={time => setForm({ ...form, heureIncident: time })}
                  format="HH:mm"
                />
              </div>
            </div>

            {/* Comment */}
            <div style={{ ...s.fieldCard, background: t.cardBg, border: `1px solid ${t.border}` }}>
              <label style={{ ...s.label, color: t.text2 }}>
                💬 Commentaire <span style={{ fontWeight: 400, color: t.text3 }}>(optionnel)</span>
              </label>
              <textarea
                rows={4}
                placeholder="Ajoutez des détails supplémentaires..."
                value={form.commentaire}
                onChange={e => setForm({ ...form, commentaire: e.target.value })}
                maxLength={500}
                style={{ width: "100%", borderRadius: 10, border: `1px solid ${t.border}`, background: t.bg3, color: t.text1, padding: "10px 12px", fontSize: 13, resize: "vertical", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
              />
              <div style={{ fontSize: 11, color: t.text3, textAlign: "right", marginTop: 4 }}>{form.commentaire.length}/500</div>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{ ...s.primaryBtn, background: submitting ? t.border : t.red, color: submitting ? t.text3 : "#fff", padding: "13px", opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? "Envoi en cours..." : "🚨 Envoyer le rapport"}
            </button>

            <div style={{ fontSize: 11, color: t.text3, textAlign: "center" }}>
              <span style={{ color: t.red }}>*</span> Champs obligatoires · 🔒 Signalement anonyme
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#f1f5f9", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff",
    green: "#16a34a", greenBg: "#f0fdf4",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    cardBg: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f",
    green: "#4ade80", greenBg: "#052e16",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    cardBg: "#1e293b",
  },
};

const s = {
  toggleTrack: { width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", flexShrink: 0 },
  toggleKnob: { width: 14, height: 14, borderRadius: "50%", position: "absolute", top: 2, left: 2, transition: "transform 0.2s" },
  outlineBtn: { fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer" },
  primaryBtn: { border: "none", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "opacity 0.15s", width: "100%" },
  actionCard: { display: "flex", alignItems: "center", gap: 14, borderRadius: 14, padding: "18px 16px", transition: "border-color 0.15s" },
  fieldCard: { borderRadius: 12, padding: "14px" },
  label: { display: "block", fontSize: 12, fontWeight: 600, marginBottom: 8 },
};